<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import FormAlert from "@/components/FormAlert.vue";
import { useAuthoringTemplates, useCreateAuthoringTemplate } from "@/queries/connection-template-authoring";
import type { ConnectionTemplateAuthoringListItem } from "@/types";
import type { Config } from "datatables.net";

const router = useRouter();

const { templates, templatesLoading: loading, templatesError: error, refetchTemplates } = useAuthoringTemplates();
const { createTemplate, createTemplatePending, createTemplateError, createTemplateReset } = useCreateAuthoringTemplate();

const searchQuery = ref("");
const selectedStatus = ref("all");

const filteredTemplates = computed(() =>
  templates.value.filter((t) => {
    const q = searchQuery.value.toLowerCase();
    const matchesSearch = !q || t.name.toLowerCase().includes(q) || (t.description ?? "").toLowerCase().includes(q);
    const matchesStatus = selectedStatus.value === "all" || t.status === selectedStatus.value;
    return matchesSearch && matchesStatus;
  }),
);

const statusBadge = (status: string) => (status === "Published" ? "bg-success" : "bg-secondary");

const tableOptions = {
  columns: [
    {
      data: "name",
      title: "Template",
      render: (d: string, _: string, row: ConnectionTemplateAuthoringListItem) =>
        `<strong>${d}</strong>${row.description ? `<br><small class="text-muted">${row.description}</small>` : ""}`,
    },
    {
      data: "status",
      title: "Status",
      render: (d: string) => `<span class="badge text-uppercase ${statusBadge(d)}">${d}</span>`,
    },
    { data: "protocol", title: "Protocol", render: (d: string | null) => d ?? "" },
    { data: "authoringLevel", title: "Level" },
  ],
  // topEnd only — external search replaces DataTables'; info + paging stay (a stripped pager
  // silently truncates at 10 rows).
  layout: { topEnd: "" },
} as Config;

// New-draft modal: name is the only required input — the draft is created empty and everything
// else is authored on the detail screen.
const showCreate = ref(false);
const newName = ref("");
const newDescription = ref("");

function openCreate() {
  newName.value = "";
  newDescription.value = "";
  createTemplateReset();
  showCreate.value = true;
}

function submitCreate() {
  createTemplate(
    {
      name: newName.value.trim(),
      authoringLevel: "Platform",
      content: { authMethods: [] },
      description: newDescription.value.trim() || null,
    },
    {
      onSuccess: (dto) => {
        showCreate.value = false;
        router.push(`/admin/connection-templates/${dto.id}`);
      },
    },
  );
}

function openTemplate(item: ConnectionTemplateAuthoringListItem) {
  router.push(`/admin/connection-templates/${item.id}`);
}
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Connection Templates"
        :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Connection Templates' }]"
      >
        <template #actions>
          <button class="bb-btn-primary-cta" @click="openCreate">
            <i class="bi bi-plus-circle me-2"></i>New<span class="d-none d-md-inline"> Template</span>
          </button>
        </template>
      </PageHeader>

      <div class="row g-3 mb-4">
        <div class="col-12 col-md-8">
          <input v-model="searchQuery" type="text" class="form-control" placeholder="Search templates..." />
        </div>
        <div class="col-12 col-md-4">
          <select v-model="selectedStatus" class="form-select">
            <option value="all">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Published">Published</option>
          </select>
        </div>
      </div>

      <SkeletonLoader v-if="loading || error" type="table" :rows="6" :error="error" @retry="refetchTemplates" />

      <DataView
        v-else
        :data="filteredTemplates"
        view-mode="table"
        :options="tableOptions"
        @row-click="openTemplate"
      >
        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-plug display-1 text-muted"></i>
            <h3 class="mt-3 text-muted">No templates found</h3>
            <button class="bb-btn-primary-cta mt-2" @click="openCreate">
              <i class="bi bi-plus me-1"></i>New Template
            </button>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>

  <div
    class="modal fade"
    :class="{ show: showCreate }"
    :style="{ display: showCreate ? 'block' : 'none' }"
    @click.self="showCreate = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">New Connection Template</h2>
          <button type="button" class="btn-close" @click="showCreate = false"></button>
        </div>
        <form @submit.prevent="submitCreate">
          <div class="modal-body">
            <FormAlert :error="createTemplateError" @dismiss="createTemplateReset" />
            <div class="mb-3">
              <label for="new-template-name" class="form-label">Name</label>
              <input id="new-template-name" v-model="newName" type="text" class="form-control" required />
            </div>
            <div class="mb-0">
              <label for="new-template-description" class="form-label">Description</label>
              <input id="new-template-description" v-model="newDescription" type="text" class="form-control" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="showCreate = false">Cancel</button>
            <button type="submit" class="bb-btn-primary-cta" :disabled="!newName.trim() || createTemplatePending">
              <span v-if="createTemplatePending" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Create Draft
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="showCreate" class="modal-backdrop fade show"></div>
</template>
